import React, { useEffect, useRef, useState } from 'react';
import { useSandpack } from '@codesandbox/sandpack-react';
import type { SandboxErrorHandler } from './SandboxErrorBoundary';

/** Wait before each automatic retry. Its length is also the number of automatic retries. */
const RETRY_DELAYS_MS = [2_000, 4_000];

// Sandpack reports a failed run through its status (which this guard watches), so the rejection adds nothing.
function rerunSandpack(run: () => Promise<void>): void {
  run().catch(() => undefined);
}

export interface SandboxTimeoutGuardProps {
  /** Called once when the automatic retries are used up (source: 'infrastructure'). */
  onError?: SandboxErrorHandler;
}

/**
 * Handles Sandpack's `timeout` status — the hosted bundler never answered (slow or blocked network,
 * brief CodeSandbox outage). Those are usually transient, so it retries on its own a couple of times,
 * then shows a plain-language message with a "Try again" button instead of Sandpack's raw
 * "Couldn't connect to server / ENV / TIME_OUT" overlay, which it covers.
 *
 * Must be rendered inside a SandboxProvider / SandpackProvider, over a `position: relative` parent.
 */
export function SandboxTimeoutGuard({ onError }: SandboxTimeoutGuardProps): React.ReactElement | null {
  const { sandpack } = useSandpack();
  const { status, runSandpack } = sandpack;
  const [exhausted, setExhausted] = useState(false);
  const attempts = useRef(0);

  // Latest callbacks without re-running the status effect (a new function identity must not restart a retry).
  const latest = useRef({ onError, runSandpack });
  useEffect(() => {
    latest.current = { onError, runSandpack };
  });

  useEffect(() => {
    if (status === 'done') {
      attempts.current = 0;
      return undefined;
    }
    if (status !== 'timeout') return undefined;

    const delay = RETRY_DELAYS_MS[attempts.current];
    // Out of retries: show the message on the next tick (state is set from a callback, not the effect body).
    const timer = setTimeout(
      () => {
        if (delay === undefined) {
          setExhausted(true);
          latest.current.onError?.(new Error('Sandbox bundler timed out'), {
            source: 'infrastructure',
            message: 'TIME_OUT',
          });
          return;
        }
        attempts.current += 1;
        rerunSandpack(latest.current.runSandpack);
      },
      delay ?? 0
    );
    return () => {
      clearTimeout(timer);
      // Leaving "timeout" (a retry started, or it recovered) ends the failed state.
      setExhausted(false);
    };
  }, [status]);

  if (status !== 'timeout') return null;

  const handleTryAgain = (): void => {
    attempts.current = 0;
    setExhausted(false);
    rerunSandpack(latest.current.runSandpack);
  };

  return (
    <div
      role="status"
      style={{
        position: 'absolute',
        inset: 0,
        // Above Sandpack's own timeout overlay (its `zIndices.top`, 3), which is shown at the same time.
        zIndex: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // Top, not centred: a tall widget would put the message out of view, mid-page.
        justifyContent: 'flex-start',
        gap: 12,
        padding: '48px 24px 24px',
        background: '#fff',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        color: '#64748b',
        textAlign: 'center',
      }}
    >
      {exhausted ? (
        <>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#334155' }}>We couldn&apos;t load this widget</div>
          <div style={{ fontSize: 12, maxWidth: 320 }}>
            This is usually a temporary network problem. Check your connection (a VPN, proxy or ad blocker can
            block it) and try again.
          </div>
          <button
            type="button"
            onClick={handleTryAgain}
            style={{
              marginTop: 4,
              padding: '6px 16px',
              fontSize: 12,
              fontWeight: 500,
              border: '1px solid #cbd5e1',
              borderRadius: 6,
              background: '#fff',
              color: '#334155',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </>
      ) : (
        <div style={{ fontSize: 12 }}>Having trouble loading this widget. Retrying…</div>
      )}
    </div>
  );
}
